import { compactSpan, dayLabel, resolveDay, shortSpan } from "@/helpers/availability";
import useFrenchHolidays from "@/hooks/useFrenchHolidays";
import { AvailabilityException, ResolvedDay, WeeklyDay } from "@/interfaces/Availability";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { FC, useMemo } from "react";

interface IProps {
  weekly: WeeklyDay[];
  exceptions: AvailabilityException[];
  /** yyyy-MM-dd, heure de Paris : les jours antérieurs ne sont plus cliquables. */
  today: string;
  /** yyyy-MM */
  month: string;
  onMonthChange: (month: string) => void;
  /** Absent en lecture seule. */
  onDayClick?: (date: string) => void;
}

const WEEKDAY_HEADERS = ["LUN", "MAR", "MER", "JEU", "VEN", "SAM", "DIM"];

type CellKind = "open" | "hours" | "closed" | "off" | "holiday";

/** L'état d'une journée, tel que la légende le nomme. */
const kindOf = (day: ResolvedDay): CellKind => {
  if (day.source === "exception") return day.ranges.length ? "hours" : "off";
  if (day.source === "holiday") return "holiday";
  return day.ranges.length ? "open" : "closed";
};

/**
 * Le texte d'une case : l'amplitude si ouvert, le motif sinon (sauf « Fermé »,
 * que la légende porte). Deux longueurs : la case d'un téléphone est étroite.
 */
const noteOf = (day: ResolvedDay, kind: CellKind) => {
  if (kind === "open" || kind === "hours") {
    return { long: compactSpan(day.ranges), short: shortSpan(day.ranges) };
  }
  if (kind === "off") return { long: "Congé", short: "Congé" };
  if (kind === "holiday") return { long: "Férié", short: "Férié" };
  return { long: "", short: "" };
};

const LEGEND: { kind: CellKind; label: string }[] = [
  { kind: "open", label: "Ouvert" },
  { kind: "closed", label: "Fermé" },
  { kind: "off", label: "Congé" },
  { kind: "hours", label: "Horaires particuliers" },
  { kind: "holiday", label: "Férié" },
];

/** Le mois tel que le planning EN COURS D'ÉDITION le rendrait, enregistré ou non. */
const AvailabilityMonthPreview: FC<IProps> = ({
  weekly,
  exceptions,
  today,
  month,
  onMonthChange,
  onDayClick,
}) => {
  // UTC : une grille de dates n'a pas d'heure, et le 25 octobre ne doit pas
  // compter 25 heures.
  const first = DateTime.fromISO(`${month}-01`, { zone: "utc" });
  const years = useMemo(() => [first.year], [first.year]);
  const { getHolidayForDate } = useFrenchHolidays(years);

  const last = first.endOf("month");
  const cells: DateTime[] = [];
  for (
    let d = first.minus({ days: first.weekday - 1 });
    d <= last.plus({ days: 7 - last.weekday });
    d = d.plus({ days: 1 })
  ) {
    cells.push(d);
  }

  return (
    <section aria-labelledby="availability-month-heading">
      <div className="fx-availability__head align-items-center">
        <h3 id="availability-month-heading" className="fx-availability__title">
          Aperçu
        </h3>
        <div className="fx-month__nav">
          <button
            type="button"
            className="fx-icon-btn"
            aria-label="Mois précédent"
            onClick={() => onMonthChange(first.minus({ months: 1 }).toFormat("yyyy-MM"))}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="fx-month__label" aria-live="polite">
            {first.setLocale("fr").toFormat("LLLL yyyy")}
          </span>
          <button
            type="button"
            className="fx-icon-btn"
            aria-label="Mois suivant"
            onClick={() => onMonthChange(first.plus({ months: 1 }).toFormat("yyyy-MM"))}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
      <p className="fx-availability__hint mb-0">
        {onDayClick
          ? "Se met à jour pendant que vous modifiez. Cliquez un jour pour y poser un congé."
          : "Le mois tel que ces disponibilités le rendent."}
      </p>

      <div aria-hidden className="fx-month__weekdays">
        {WEEKDAY_HEADERS.map((header) => (
          <span key={header}>{header}</span>
        ))}
      </div>

      <ul className="fx-month__grid">
        {cells.map((cell) => {
          const date = cell.toISODate()!;
          if (cell.month !== first.month) {
            return <li key={date} aria-hidden className="fx-cell-filler" />;
          }

          const holiday = getHolidayForDate(date);
          const day = resolveDay(weekly, exceptions, date, holiday?.name);
          const kind = kindOf(day);
          const short = dayLabel(day);
          const spoken =
            day.source === "holiday" ? `Férié (${day.holidayName})` : short;
          const label = `${cell.setLocale("fr").toFormat("cccc d LLLL yyyy")} : ${spoken}`;
          const interactive = !!onDayClick && date >= today;
          const className = [
            "fx-cell",
            `fx-cell--${kind}`,
            date < today ? "fx-cell--past" : "",
            date === today ? "fx-cell--today" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const note = noteOf(day, kind);
          const content = (
            <>
              <span className="fx-cell__day">{cell.day}</span>
              <span className="fx-cell__note">
                <span className="fx-cell__long">{note.long}</span>
                <span className="fx-cell__short" aria-hidden>
                  {note.short}
                </span>
              </span>
            </>
          );

          return (
            <li key={date} aria-label={interactive ? undefined : label} title={holiday?.name}>
              {interactive ? (
                <button
                  type="button"
                  aria-label={label}
                  className={className}
                  onClick={() => onDayClick!(date)}
                >
                  {content}
                </button>
              ) : (
                <div className={className}>{content}</div>
              )}
            </li>
          );
        })}
      </ul>

      <ul className="fx-month__legend" aria-label="Légende">
        {LEGEND.map(({ kind, label }) => (
          <li key={kind}>
            <span aria-hidden className={`fx-month__swatch fx-cell--${kind}`} />
            {label}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default AvailabilityMonthPreview;
